import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import clsx from "clsx";
import { makeStyles, ListItem, Typography, Badge, IconButton, Tooltip } from "@material-ui/core";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import useNow from "../../hooks/useNow";
import { getWaitingState } from "../../utils/waitingTime";
import WaitingStatus, { useWaitingStyles } from "../WaitingStatus";
import ContactAvatar from "../ContactAvatar";
import { contactDisplayName } from "../../utils/contactPresentation";
import ExactTime from "../ExactTime";
import TicketPreviewModal from "../TicketPreviewModal";

const useStyles = makeStyles(theme => ({
  ticket: { position: "relative", flexDirection: "column", alignItems: "stretch", borderRadius: 12, border: "1px solid transparent", margin: "6px 8px", padding: "11px 12px", width: "auto", transition: "background-color .15s ease", "&:hover": { backgroundColor: theme.custom.surfaceHover }, "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: theme.custom.sidebarActiveBg } },
  pending: { backgroundColor: theme.custom.surface },
  row: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  body: { flex: 1, minWidth: 0 },
  name: { fontWeight: 700, fontSize: ".83rem" },
  message: { fontSize: ".77rem", marginTop: 2, color: theme.palette.text.secondary },
  time: { display: "block", fontSize: ".65rem", color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums", marginTop: 3 },
  details: { color: theme.palette.text.secondary, padding: 5 },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginTop: 8, "& > span": { marginTop: 0 } },
  tags: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 5, marginTop: 8 },
  tag: { display: "inline-flex", alignItems: "center", gap: 4, maxWidth: 140, borderRadius: 6, padding: "2px 6px", background: theme.custom.sidebarActiveBg, color: theme.palette.text.secondary, fontSize: ".63rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  dot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
  accept: { borderRadius: 8, marginLeft: "auto", fontSize: ".7rem", padding: "4px 12px" },
  badge: { backgroundColor: theme.palette.primary.main, color: "#fff", fontSize: ".65rem" },
}));

const TicketListItem = ({ ticket }) => {
  const classes = useStyles();
  const waitingClasses = useWaitingStyles();
  const history = useHistory();
  const { ticketId } = useParams();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isMounted = useRef(true);
  const pending = ticket.status === "pending";
  const now = useNow(pending);
  const wait = getWaitingState(ticket, now);

  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  const handleAcceptTicket = async id => {
    if (loading) return;
    setLoading(true);
    try {
      await api.put("/tickets/" + id, { status: "open", userId: user?.id });
      if (isMounted.current) { setLoading(false); setPreviewOpen(false); }
      history.push("/tickets/" + id);
    } catch (err) {
      if (isMounted.current) setLoading(false);
      toastError(err);
    }
  };

  return <>
    <ListItem dense button={!pending} onClick={() => { if (!pending) history.push("/tickets/" + ticket.id); }} selected={ticketId && +ticketId === ticket.id}
      className={clsx(classes.ticket, { [classes.pending]: pending, [waitingClasses.frame]: pending, [waitingClasses[wait.level]]: pending })}>
      <div className={classes.row}>
        <Badge badgeContent={ticket.unreadMessages} classes={{ badge: classes.badge }}><ContactAvatar contact={ticket.contact} /></Badge>
        <div className={classes.body}>
          <Typography noWrap className={classes.name}>{contactDisplayName(ticket.contact)}</Typography>
          <Typography noWrap className={classes.message}>{ticket.lastMessage ? <MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper> : "Nenhuma mensagem"}</Typography>
          <ExactTime value={ticket.lastMessageAt} className={classes.time} />
        </div>
        <Tooltip title="Ver detalhes do atendimento" arrow><IconButton size="small" className={classes.details} aria-label={"Ver detalhes de " + contactDisplayName(ticket.contact)} onClick={event => { event.stopPropagation(); setPreviewOpen(true); }}><VisibilityOutlinedIcon fontSize="small" /></IconButton></Tooltip>
      </div>
      <div className={classes.tags}>
        <span className={classes.tag} title={ticket.queue?.name || "Sem fila"}><span className={classes.dot} style={{ backgroundColor: ticket.queue?.color || "#94A3B8" }} />{ticket.queue?.name || "Sem fila"}</span>
        {ticket.user && ticket.userId !== user?.id && <span className={classes.tag} title={i18n.t("ticketsList.assignedTitle")}>{ticket.user.name}</span>}
        {ticket.whatsapp?.name && <span className={classes.tag} title={i18n.t("ticketsList.connectionTitle")}>{ticket.whatsapp.name}</span>}
        {ticket.status === "closed" && <span className={classes.tag}>Concluído</span>}
      </div>
      {pending && <div className={classes.footer}>
        <WaitingStatus ticket={ticket} now={now} />
        <ButtonWithSpinner color="primary" variant="contained" className={classes.accept} size="small" loading={loading} onClick={event => { event.stopPropagation(); handleAcceptTicket(ticket.id); }}>{i18n.t("ticketsList.buttons.accept")}</ButtonWithSpinner>
      </div>}
    </ListItem>
    {previewOpen && <TicketPreviewModal ticket={ticket} open onClose={() => setPreviewOpen(false)} onAccept={handleAcceptTicket} accepting={loading} />}
  </>;
};
export default TicketListItem;
